import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, RefreshCw, Route, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { PrintDestinationRole } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Select } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  FLEET_PERMISSIONS,
  FLEET_ROUTES,
  type FleetDestination,
  type FleetPrinter,
  type FleetRoutingRule,
} from '../fleetApi';
import { PrinterStatusChip } from '../fleetVisuals';
import { apiMessage, useBranches, useQueryParams } from '../fleetHelpers';
import PanelResult from './PanelResult';
import DestinationFormDialog from './DestinationFormDialog';
import RoutingRuleFormDialog, { type StationRef, type CategoryRef } from './RoutingRuleFormDialog';

const ROLES = Object.values(PrintDestinationRole);

/**
 * ## Destinations, and the rules that point at them
 *
 * The indirection is the whole design, so the two sit on one screen: a rule
 * chooses a **destination** — "Hot line" — and the destination chooses the
 * hardware. Swapping a dead printer for the spare is then one edit here rather
 * than a rewrite of every rule that named the dead one.
 *
 * **A rule never re-decides which station makes a dish.** `KitchenStation` and
 * `OrderItem.stationId` remain the only authority on that. A rule takes the
 * station the kitchen already chose and answers the strictly narrower question
 * of which paper that station's ticket comes out on. Two opinions about where a
 * dessert is made would leave a cook holding a board and a ticket that disagree,
 * with nothing able to say which is right — so the form's station field is
 * labelled as a *match*, not an assignment.
 *
 * **Every criterion is nullable and null means "any".** Specificity — the count
 * of criteria a rule actually pins — breaks ties below `priority`, so a rule
 * about one category beats a blanket rule without anybody hand-numbering them.
 * The table shows both, because a rule that never fires is otherwise invisible.
 *
 * **A destination with no printer is called out.** The role is what routing
 * falls back to when no rule matches, so a venue that never writes a rule still
 * prints — but only if a destination for that role has hardware behind it. A
 * destination pointing at nothing is where kitchen tickets go to disappear, and
 * it is drawn as a fault rather than left to be noticed.
 */
export default function RoutingPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [role, setRole] = useState('');
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState<FleetDestination | null>(null);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FleetRoutingRule | null>(null);

  const canCreate = can(FLEET_PERMISSIONS.printersCreate);
  const canUpdate = can(FLEET_PERMISSIONS.printersUpdate);
  // The two rule criteria live in other modules and are gated on their own keys.
  // A printing administrator who does not hold them still gets a usable form —
  // the criterion is simply not offered — rather than a 403 they cannot act on.
  const canReadStations = can('kitchen.view');
  const canReadCategories = can('menu.view');

  const branchParams = useQueryParams({ branchId });
  const destinationsQuery = useApi<FleetDestination[]>(
    FLEET_ROUTES.destinations,
    useQueryParams({ branchId, role }),
  );
  const rulesQuery = useApi<FleetRoutingRule[]>(FLEET_ROUTES.routingRules, branchParams);
  const printersQuery = useApi<unknown>(
    FLEET_ROUTES.printers,
    useQueryParams({ branchId, limit: 200 }),
  );
  const stationsQuery = useApi<StationRef[]>(
    canReadStations ? '/kitchen' : null,
    useQueryParams({ branchId, limit: 200 }),
  );
  const categoriesQuery = useApi<unknown>(canReadCategories ? '/categories' : null, { limit: 200 });

  const destinations = useMemo(() => destinationsQuery.data ?? [], [destinationsQuery.data]);
  const rules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);
  const printers = rowsOf<FleetPrinter>(printersQuery.data).rows;
  const stations = useMemo(() => stationsQuery.data ?? [], [stationsQuery.data]);
  const categories = rowsOf<CategoryRef>(categoriesQuery.data).rows;

  const reload = () => {
    destinationsQuery.reload();
    rulesQuery.reload();
    printersQuery.reload();
  };

  const removeRule = async (rule: FleetRoutingRule) => {
    const ok = await confirm.confirm({
      title: t('fleet.rules.deleteTitle'),
      body: t('fleet.rules.deleteConfirm', {
        destination: rule.destination?.name ?? t('fleet.common.unknownDestination'),
      }),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(FLEET_ROUTES.routingRule(rule.id));
      toast.success(t('fleet.rules.deleted'));
      reload();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    }
  };

  /** How many criteria a rule actually pins — the tie-break below `priority`. */
  const specificity = (rule: FleetRoutingRule) =>
    [rule.documentType, rule.stationId, rule.categoryId, rule.fulfillmentType].filter(Boolean)
      .length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            aria-label={t('fleet.common.branch')}
          >
            <option value="">{t('fleet.common.allBranches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          className="w-auto min-w-[10rem]"
          value={role}
          onChange={(event) => setRole(event.target.value)}
          aria-label={t('fleet.destinations.role')}
        >
          <option value="">{t('fleet.destinations.allRoles')}</option>
          {ROLES.map((option) => (
            <option key={option} value={option}>
              {t(`fleet.role.${option}`)}
            </option>
          ))}
        </Select>
        <Button variant="outline" size="sm" onClick={reload}>
          <Icon as={RefreshCw} />
          {t('fleet.common.refresh')}
        </Button>
        <div className="ms-auto flex flex-wrap gap-2">
          {canUpdate && (
            <Button
              variant="outline"
              onClick={() => {
                setEditingRule(null);
                setRuleOpen(true);
              }}
              disabled={destinations.length === 0}
              title={destinations.length === 0 ? t('fleet.rules.needsDestination') : undefined}
            >
              <Icon as={Route} />
              {t('fleet.rules.add')}
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => {
                setEditingDestination(null);
                setDestinationOpen(true);
              }}
            >
              <Icon as={Plus} />
              {t('fleet.destinations.add')}
            </Button>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">{t('fleet.destinations.heading')}</h2>
        <p className="text-sm text-ink-muted">{t('fleet.destinations.explainer')}</p>
        <PanelResult
          loading={destinationsQuery.loading && !destinationsQuery.data}
          error={destinationsQuery.error}
          count={destinations.length}
          onRetry={reload}
          subject={t('fleet.destinations.subject')}
          filtered={Boolean(branchId || role)}
          onClearFilters={() => {
            setBranchId('');
            setRole('');
          }}
          emptyTitle={t('fleet.destinations.emptyTitle')}
          emptyHint={t('fleet.destinations.emptyHint')}
          emptyAction={
            canCreate ? (
              <Button
                onClick={() => {
                  setEditingDestination(null);
                  setDestinationOpen(true);
                }}
              >
                <Icon as={Plus} />
                {t('fleet.destinations.add')}
              </Button>
            ) : undefined
          }
        >
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('fleet.common.destination')}</TH>
                  <TH>{t('fleet.destinations.role')}</TH>
                  <TH>{t('fleet.destinations.primary')}</TH>
                  <TH>{t('fleet.destinations.fallback')}</TH>
                  <TH>{t('fleet.destinations.copies')}</TH>
                  <TH className="text-end">{t('fleet.common.actions')}</TH>
                </TR>
              </THead>
              <tbody>
                {destinations.map((destination) => {
                  // No primary and no fallback: this is where documents go to
                  // disappear, so it is drawn as the fault it is.
                  const orphaned =
                    destination.isActive && !destination.printerId && !destination.fallbackPrinterId;
                  return (
                    <TR
                      key={destination.id}
                      className={cn(
                        'border-s-4',
                        orphaned
                          ? 'border-s-red-500 bg-red-50/40 dark:bg-red-950/20'
                          : 'border-s-transparent',
                        !destination.isActive && 'opacity-60',
                      )}
                    >
                      <TD>
                        <span className="font-medium">{destination.name}</span>
                        <p className="text-xs text-ink-muted">{destination.code}</p>
                        {!destination.isActive && (
                          <Badge tone="neutral" className="mt-1">
                            {t('fleet.destinations.inactive')}
                          </Badge>
                        )}
                      </TD>
                      <TD>
                        <Badge tone="neutral">{t(`fleet.role.${destination.role}`)}</Badge>
                      </TD>
                      <TD>
                        {destination.printer ? (
                          <div className="flex flex-col items-start gap-1">
                            <span>{destination.printer.name}</span>
                            <PrinterStatusChip status={destination.printer.lastStatus ?? 'UNKNOWN'} />
                          </div>
                        ) : (
                          <span className="text-red-700 dark:text-red-400">
                            {t('fleet.destinations.noPrinter')}
                          </span>
                        )}
                      </TD>
                      <TD>
                        {destination.fallbackPrinter ? (
                          destination.fallbackPrinter.name
                        ) : (
                          <span className="text-ink-muted">{t('fleet.common.dash')}</span>
                        )}
                      </TD>
                      <TD className="tabular-nums">{destination.copies}</TD>
                      <TD>
                        <div className="flex justify-end gap-1">
                          {canUpdate && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={t('common.edit')}
                              onClick={() => {
                                setEditingDestination(destination);
                                setDestinationOpen(true);
                              }}
                            >
                              <Icon as={Pencil} />
                              <span className="sr-only">{t('common.edit')}</span>
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        </PanelResult>
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">{t('fleet.rules.heading')}</h2>
        <p className="text-sm text-ink-muted">{t('fleet.rules.explainer')}</p>
        <PanelResult
          loading={rulesQuery.loading && !rulesQuery.data}
          error={rulesQuery.error}
          count={rules.length}
          onRetry={reload}
          subject={t('fleet.rules.subject')}
          filtered={Boolean(branchId)}
          onClearFilters={() => setBranchId('')}
          emptyTitle={t('fleet.rules.emptyTitle')}
          emptyHint={t('fleet.rules.emptyHint')}
        >
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('fleet.rules.priority')}</TH>
                  <TH>{t('fleet.rules.matches')}</TH>
                  <TH>{t('fleet.rules.printsAt')}</TH>
                  <TH>{t('fleet.common.status')}</TH>
                  <TH className="text-end">{t('fleet.common.actions')}</TH>
                </TR>
              </THead>
              <tbody>
                {rules.map((rule) => (
                  <TR
                    key={rule.id}
                    className={cn('border-s-4 border-s-transparent', !rule.isActive && 'opacity-60')}
                  >
                    <TD className="tabular-nums">
                      {rule.priority}
                      <p className="text-xs text-ink-muted">
                        {t('fleet.rules.specificity', { count: specificity(rule) })}
                      </p>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        {rule.documentType && (
                          <Badge tone="info">
                            {t(`fleet.documentType.${rule.documentType}`)}
                          </Badge>
                        )}
                        {rule.station && <Badge tone="neutral">{rule.station.name}</Badge>}
                        {rule.category && <Badge tone="neutral">{rule.category.name}</Badge>}
                        {rule.fulfillmentType && (
                          <Badge tone="neutral">
                            {t(`fleet.fulfillment.${rule.fulfillmentType}`)}
                          </Badge>
                        )}
                        {specificity(rule) === 0 && (
                          <span className="text-sm text-ink-muted">
                            {t('fleet.rules.matchesEverything')}
                          </span>
                        )}
                      </div>
                    </TD>
                    <TD>
                      <span className="font-medium">
                        {rule.destination?.name ?? t('fleet.common.unknownDestination')}
                      </span>
                      {rule.destination && (
                        <p className="text-xs text-ink-muted">
                          {t(`fleet.role.${rule.destination.role}`)}
                        </p>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={rule.isActive ? 'success' : 'neutral'}>
                        {rule.isActive ? t('fleet.rules.active') : t('fleet.rules.inactive')}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('common.edit')}
                            onClick={() => {
                              setEditingRule(rule);
                              setRuleOpen(true);
                            }}
                          >
                            <Icon as={Pencil} />
                            <span className="sr-only">{t('common.edit')}</span>
                          </Button>
                        )}
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('common.delete')}
                            onClick={() => void removeRule(rule)}
                          >
                            <Icon as={Trash2} className="text-red-600 dark:text-red-400" />
                            <span className="sr-only">{t('common.delete')}</span>
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </PanelResult>
      </section>

      <DestinationFormDialog
        open={destinationOpen}
        destination={editingDestination}
        branches={branches}
        printers={printers}
        defaultBranchId={branchId || undefined}
        onClose={() => setDestinationOpen(false)}
        onSaved={reload}
      />
      <RoutingRuleFormDialog
        open={ruleOpen}
        rule={editingRule}
        destinations={destinations}
        stations={stations}
        categories={categories}
        canReadStations={canReadStations}
        canReadCategories={canReadCategories}
        locale={i18n.language}
        onClose={() => setRuleOpen(false)}
        onSaved={reload}
      />
    </div>
  );
}
